import { httpClientPrivate } from '@/libs/httpClient'
import config from '@/config'
import { useMutation, useQueryClient } from '@tanstack/react-query'

// Error codes returned by the assign-crash-detection-owner endpoint.
export enum AssignCrashDetectionOwnerErrorCode {
  CASE_NOT_FOUND = 'BIZCRASH1006', // crash case not found
  LOCKED_BY_OTHER_OWNER = 'BIZBOCRASH1001', // locked by another owner within the 2-hour timeout
  LOCKED_BY_CURRENT_USER_OTHER_CASE = 'BIZBOCRASH1002', // locked by current user on another case
}

// `data` payload returned in the BIZBOCRASH1001 error body — identifies the
// current owner holding the lock (the source of truth for the locked modal).
export interface AssignCrashDetectionOwnerErrorData {
  ownerName?: string
  ownerEmployeeCode?: string
  crashDetectionCrashInfoId?: string
}

// POST /v1/backoffice/crash-detection/assign-crash-detection-owner/:crashDetectionCrashInfoId
// No request body, responds 204 No Content. Idempotent re-claim by the same owner succeeds silently.
const assignCrashDetectionOwner = async (crashDetectionCrashInfoId: string): Promise<void> => {
  await httpClientPrivate.post(
    `${config.restUrl}/v1/backoffice/crash-detection/assign-crash-detection-owner/${crashDetectionCrashInfoId}`,
  )
}

const useAssignCrashDetectionOwner = () => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (crashDetectionCrashInfoId: string) =>
      assignCrashDetectionOwner(crashDetectionCrashInfoId),
    onSuccess: (_, crashDetectionCrashInfoId) => {
      queryClient.invalidateQueries({ queryKey: ['useCrashDetectionList'] })
      queryClient.invalidateQueries({
        queryKey: ['useCrashDetectionDetail', crashDetectionCrashInfoId],
      })
    },
  })

  return { ...mutation }
}

export { assignCrashDetectionOwner, useAssignCrashDetectionOwner }
